import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Alert,
  AlertDescription,
  toast,
  DialogFooter,
  AlertTitle,
  Typography,
} from "@altimateai/lego";
import { CopyButton } from "../copyButton";
import { useMutation } from "@tanstack/react-query";
import { createJiraTicket } from "@/helpers/apis";
import { DatastoreTypeEnum } from "@/pages/datastores/types";
import { Teammate } from "@/pages/mcp/mcpSlice";

import { Spinner, Stack } from "@altimateai/lego";
import { OpportunityDetails } from "@/components/closingLoop/types";
import { useAppState } from "@/modules/app/useAppContext";
import { SetupRequiredAlert } from "@/components/closingLoop/common/SetupRequiredAlert";
import { DatamatesSelector } from "@/components/closingLoop/common/DatamatesSelector";
import { FirstTimeSetupAlert } from "@/components/closingLoop/common/FirstTimeSetupAlert";
import { HelpSection } from "@/components/closingLoop/common/HelpSection";
import useTeammates from "@/pages/mcp/useTeammates";
import ExpandableText from "../expandableText/ExpandableText";
import { isFreeStack } from "@/helpers/tenant";

interface AssignToDatamateModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: OpportunityDetails;
  integrationIds?: string[];
}

export default function AssignToDatamateModal({
  isOpen,
  onClose,
  opportunity,
  integrationIds = ["altimate"],
}: AssignToDatamateModalProps) {
  const [selectedDatamate, setSelectedDatamate] = useState<string>("");
  const [datamates, setDatamates] = useState<Teammate[]>([]);
  const [prompt, setPrompt] = useState<string>("");
  const [selectedDatamateName, setSelectedDatamateName] = useState<string>("");
  const { datastores } = useAppState();
  const { data: datamatesData, isPending: isLoadingDatamates } = useTeammates(isOpen);

  const datastoresData = useMemo(() => {
    return datastores?.filter(datastore => datastore.type === DatastoreTypeEnum.SNOWFLAKE) ?? [];
  }, [datastores]);

  useEffect(() => {
    setDatamates(
      // Filter datamates which have specified integrations
      datamatesData?.filter(datamate => {
        return datamate.integrations?.some(integration => {
          return integrationIds.includes(integration.id);
        });
      }) || []
    );
  }, [datamatesData, integrationIds]);

  const { mutate: jiraTicketMutation, isPending: isPropmtLoading } = useMutation({
    mutationFn: () =>
      createJiraTicket(
        opportunity.impact?.opportunity.opportunity_id ?? opportunity.opportunity_id,
        opportunity.impact?.query_id,
        false,
        "solve_issue"
      ),
    onSuccess: data => {
      setPrompt(data?.content ?? "");
    },
    onError: error => {
      toast({
        title: "Failed to create Jira ticket",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    setSelectedDatamateName(() => {
      return datamates.filter(datamate => datamate.id === selectedDatamate)[0]?.name || "";
    });
  }, [datamates, selectedDatamate]);

  useEffect(() => {
    jiraTicketMutation();
  }, [jiraTicketMutation]);

  const hasConnections = (datastoresData && datastoresData.length > 0) || isFreeStack();
  const hasDatamates = datamatesData && datamatesData.length > 0;
  const singleConnection = datastoresData?.length === 1;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent
        className={"al-max-h-[80vh] al-overflow-y-auto !al-max-w-[640px]"}
        onClick={e => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Open in IDE</DialogTitle>
        </DialogHeader>

        <div className="al-flex-1 al-space-y-5">
          {/* Opportunity Summary */}
          <Alert variant={"info"}>
            <AlertTitle className={"al-text-base al-flex al-gap-2 al-items-center"}>
              {opportunity.title}
              {opportunity.savings && (
                <Badge variant="secondary" className="al-text-xs">
                  Save ${opportunity.savings.toFixed(2)}/mo
                </Badge>
              )}
            </AlertTitle>
            <AlertDescription>
              <ExpandableText buttonClassName="al-text-primary al-text-[10px]" lines={3}>
                {opportunity.description}
              </ExpandableText>
            </AlertDescription>
          </Alert>

          {/* Select Datamate Section */}
          <div className="al-space-y-3">
            <Typography variant={"h3"} weight={"semibold"}>
              Select Datamate
            </Typography>
            {/* Loading states */}
            {isLoadingDatamates && (
              <Stack className="al-items-center">
                <Spinner size="sm" />
                <span>Loading datamates...</span>
              </Stack>
            )}

            {/* No connections state */}
            {!hasConnections && <SetupRequiredAlert />}

            {/* Datamate selection for existing users */}
            {!isLoadingDatamates && hasConnections && (
              <div className="al-p-3 al-bg-gray-50 al-rounded-lg">
                <DatamatesSelector
                  datamates={datamates}
                  selectedDatamate={selectedDatamate}
                  onChange={setSelectedDatamate}
                />
              </div>
            )}

            {/* First-time user with multiple connections */}
            {!isLoadingDatamates && !hasDatamates && hasConnections && !singleConnection && (
              <FirstTimeSetupAlert />
            )}
          </div>

          {/* IDE Setup Instructions */}
          <div className="al-space-y-3">
            <h3 className="al-font-semibold al-text-base">Setup in Your Development Environment</h3>

            {/* @ts-ignore - Tabs component children prop type issue */}
            <Tabs defaultValue="cursor" className="al-w-full">
              <TabsList className="al-grid al-w-full al-grid-cols-2">
                <TabsTrigger value="cursor">Cursor</TabsTrigger>
                <TabsTrigger value="vscode">Visual Studio Code</TabsTrigger>
              </TabsList>
              <TabsContent value="cursor" className="al-space-y-3">
                <Alert>
                  <AlertDescription className="al-text-sm">
                    <strong>Setup Instructions for Cursor:</strong>
                    <ol className="al-mt-2 al-ml-4 al-list-decimal al-space-y-1">
                      <li>
                        Install & Configure the{" "}
                        <a
                          rel={"noreferrer"}
                          href="https://marketplace.visualstudio.com/items?itemName=altimateai.vscode-altimate-mcp-server"
                          target="_blank"
                          className="al-text-blue-600 al-underline"
                        >
                          Datamates Extension
                        </a>{" "}
                        in your IDE.{" "}
                        <a
                          rel={"noreferrer"}
                          href="https://app.supademo.com/showcase/cmcb0vtlu00erxg0i24chop8o?demo=3&step=1"
                          target="_blank"
                          className="al-text-blue-600 al-underline"
                        >
                          Steps here
                        </a>
                      </li>
                      <li>
                        Use{" "}
                        <code className="al-bg-gray-100 al-px-1 al-rounded">
                          {" "}
                          {selectedDatamateName && selectedDatamateName.length
                            ? selectedDatamateName
                            : "the selected datamate"}
                        </code>{" "}
                        Datamate in your editor to access your AI teammate
                      </li>
                      <li>
                        Copy the prompt below and paste it into your editor&#39;s Agent to start
                        optimization
                      </li>
                    </ol>
                  </AlertDescription>
                </Alert>
                <div className="al-border al-rounded-lg al-p-3">
                  <Stack align={"center"} justify={"between"} className={"al-mb-2"}>
                    <span className="al-text-sm al-font-medium">Optimization Prompt</span>
                    {isPropmtLoading ? <div /> : <CopyButton value={prompt} size="sm" />}
                  </Stack>
                  {isPropmtLoading ? (
                    <Stack className="al-items-center">
                      <Spinner size="sm" />
                      <span>Loading prompt...</span>
                    </Stack>
                  ) : (
                    <div className="al-relative al-p-3 al-bg-gray-100 al-rounded-md al-max-h-80 al-overflow-auto al-font-mono">
                      <pre className="al-text-xs al-m-0 al-whitespace-pre-wrap al-max-w-[500px]">
                        {prompt}
                      </pre>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="vscode" className="al-space-y-3">
                <Alert>
                  <AlertDescription className="al-text-sm">
                    <strong>Setup Instructions for Visual Studio Code:</strong>
                    <ol className="al-mt-2 al-ml-4 al-list-decimal al-space-y-1">
                      <li>
                        Install the{" "}
                        <a
                          rel={"noreferrer"}
                          href="https://marketplace.visualstudio.com/items?itemName=altimateai.vscode-altimate-mcp-server"
                          target="_blank"
                          className="al-text-blue-600 al-underline"
                        >
                          Datamates Extension
                        </a>{" "}
                        in your IDE.
                      </li>
                      <li>
                        Configure it for your preferred AI Agent -{" "}
                        <a
                          rel={"noreferrer"}
                          href="https://app.supademo.com/showcase/cmcb0vtlu00erxg0i24chop8o?demo=5&step=1"
                          target="_blank"
                          className="al-text-blue-600 al-underline"
                        >
                          Cline
                        </a>{" "}
                        or{" "}
                        <a
                          rel={"noreferrer"}
                          href="https://app.supademo.com/showcase/cmcb0vtlu00erxg0i24chop8o?demo=4&step=1"
                          target="_blank"
                          className="al-text-blue-600 al-underline"
                        >
                          Github Copilot
                        </a>
                      </li>
                      <li>
                        Use{" "}
                        <code className="al-bg-gray-100 al-px-1 al-rounded">
                          {" "}
                          {selectedDatamateName && selectedDatamateName.length
                            ? selectedDatamateName
                            : "the selected datamate"}
                        </code>{" "}
                        Datamate in your editor to access your AI teammate
                      </li>
                      <li>
                        Copy the prompt below and paste it into your editor&#39;s Agent to start
                        optimization
                      </li>
                    </ol>
                  </AlertDescription>
                </Alert>
                <div className="al-border al-rounded-lg al-p-3">
                  <Stack align={"center"} justify={"between"} className={"al-mb-2"}>
                    <span className="al-text-sm al-font-medium">Optimization Prompt</span>
                    {isPropmtLoading ? <div /> : <CopyButton value={prompt} size="sm" />}
                  </Stack>
                  {isPropmtLoading ? (
                    <Stack className="al-items-center">
                      <Spinner size="sm" />
                      <span>Loading prompt...</span>
                    </Stack>
                  ) : (
                    <div
                      className="al-relative al-p-3 al-bg-gray-50 al-rounded al-max-h-80 al-overflow-auto"
                      style={{ fontFamily: "monospace" }}
                    >
                      <pre
                        className="al-text-xs al-max-w-[500px]"
                        style={{ whiteSpace: "pre-wrap", margin: 0 }}
                      >
                        {prompt}
                      </pre>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Help Section - Collapsible */}
          <HelpSection />
        </div>

        {/* Action Buttons */}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
